"use client";

import type { ReactNode } from "react";
import type { ProcessedCourse } from "@/common/course/types";
import CurrencyBar from "@/components/CurrencySelector/CurrencyBar";
import GeoPricingRibbonBanner from "@/components/GeoPricing/GeoPricingRibbonBanner";
import { resolveGeoPricingRibbonCopy } from "@/components/GeoPricing/utils/resolveGeoPricingRibbonCopy";
import {
  DEFAULT_CURRENCY_BAR_LABEL,
  DEFAULT_CURRENCY_BAR_MOBILE_LABEL,
} from "@/components/CurrencySelector/CurrencyBar/constants";
import type { DetailPricingData } from "@/lib/checkout-geo-pricing";
import { getProgramPricingAmounts } from "@/lib/currency/getProgramPricingAmounts";
import { FeatureFlagComponentResolverClient } from "@/lib/feature-flags/FeatureFlagResolverClient";
import { useSingleCourseTrafficConfig } from "@/monarch/rules/singleCourseTraffic/useSingleCourseTraffic";

type CourseCurrencyBarSurfaceProps = {
  course: ProcessedCourse;
  pricingData?: DetailPricingData | null;
  courseIsEnrollable?: boolean;
  isArchived?: boolean;
  description?: ReactNode;
};

export function CourseCurrencyBarSurface({
  course,
  pricingData,
  courseIsEnrollable = true,
  isArchived = false,
  description,
}: CourseCurrencyBarSurfaceProps) {
  const { currencySelector, showSideRail } = useSingleCourseTrafficConfig({
    course,
  });
  const amounts = getProgramPricingAmounts(pricingData);
  const checkoutGeoPricing = pricingData?.checkoutGeoPricingEligibility;
  const isUserEligible = checkoutGeoPricing?.eligible;
  const ribbonBanner = pricingData?.geoPricing?.ribbonBanner;

  const hasMultiCurrencyStickyBar =
    currencySelector?.placement === "currency-bar" &&
    amounts != null &&
    courseIsEnrollable &&
    !isArchived;

  const isGeoPricingEnabled = Boolean(
    pricingData?.geoPricing !== undefined &&
      isUserEligible &&
      ribbonBanner &&
      checkoutGeoPricing,
  );

  let label = currencySelector?.label ?? DEFAULT_CURRENCY_BAR_LABEL;
  let mobileLabel =
    currencySelector?.mobileLabel ?? DEFAULT_CURRENCY_BAR_MOBILE_LABEL;
  let tooltip = currencySelector?.tooltip ?? "";
  let badgeText = currencySelector?.badgeText ?? "";

  if (isGeoPricingEnabled && checkoutGeoPricing && ribbonBanner) {
    label = resolveGeoPricingRibbonCopy(ribbonBanner.text, checkoutGeoPricing);
    mobileLabel = resolveGeoPricingRibbonCopy(
      ribbonBanner.text,
      checkoutGeoPricing,
    );
    tooltip = resolveGeoPricingRibbonCopy(
      ribbonBanner.tooltipText,
      checkoutGeoPricing,
    );
    badgeText = resolveGeoPricingRibbonCopy(
      ribbonBanner.badgeText,
      checkoutGeoPricing,
    );
  }

  const geoRibbonBanner = isGeoPricingEnabled ? (
    <GeoPricingRibbonBanner
      badgeText={badgeText}
      label={label}
      tooltip={tooltip}
      location="course-detail-page"
    />
  ) : null;

  if (!hasMultiCurrencyStickyBar) {
    return geoRibbonBanner;
  }

  return (
    <FeatureFlagComponentResolverClient
      flagKey="NEXT_PUBLIC_FEATURE_MULTI_CURRENCY_ACCEPTANCE"
      variants={{
        legacy: geoRibbonBanner,
        test: amounts ? (
          <CurrencyBar
            price={amounts.price}
            originalPrice={amounts.originalPrice}
            badgeText={badgeText}
            label={label}
            mobileLabel={mobileLabel}
            tooltip={tooltip}
            description={description}
            location="single-course-detail-page"
            wideLabelGap={isGeoPricingEnabled}
            countryAutoDetection={currencySelector?.countryAutoDetection}
            compactDismiss={!!showSideRail}
          />
        ) : null,
      }}
      fallback="legacy"
    />
  );
}
